// Conteúdo por etapa da Auto Configuração da Entidade (roadmap #46).
// Fonte unica do texto explicativo exibido no assistente; o SQL guarda apenas
// metadados de fluxo (chave/titulo/rota/ordem/flags).
// Cada chave (mesma do catálogo `autoconfiguracao_etapas` no backend) define:
//   - info: JSX do painel ESQUERDO (o que é, por que configurar, exemplos);
//   - Componente: tela real embutida no painel DIREITO (quando o encaixe é viável);
//   - tipo "modal-preferencias": painel direito abre EmpresasConfiguracoesModal
//     na aba indicada (plano/geral);
//   - tipo "manual": sem embed viável (telas que dependem de classificacao no
//     localStorage) — painel direito explica e oferece "Abrir tela completa".
import React from "react";

import GruposUsuario from "../GruposUsuario/GruposUsuario";
import Usuarios from "../Usuarios/Usuarios";
import TiposAgendamento from "../TiposAgendamento/TiposAgendamento";
import TiposPagamento from "../TiposPagamento/TiposPagamento";
import TiposFornecedor from "../TiposFornecedor/TiposFornecedor";
import Funcionarios from "../Funcionarios/Funcionarios";
import IndicesCorrecao from "../IndicesCorrecao/IndicesCorrecao";
import CamposCustomizados from "../CamposCustomizados/CamposCustomizados";
import EmailNotificacao from "../Configuracoes/EmailNotificacao/EmailNotificacao";
import ClassificacaoTiposDocumento from "../ClassificacaoTiposDocumento/ClassificacaoTiposDocumento";
import TiposDocumento from "../TiposDocumento/TiposDocumento";
import Documentos from "../Documentos/Documentos";
import Empresas from "../Empresas/Empresas";

const Fornecedores = () => <Empresas classificacaoOverride="F" />;
const Filiais = () => <Empresas classificacaoOverride="V" />;

const TIPOS_AGENDAMENTO_CONTEUDO = {
  info: (
    <>
      <p>
        Os tipos de agendamento definem <strong>quando os alertas de
        vencimento sao disparados</strong> e quais usuarios devem ser
        notificados.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui alguns <strong>exemplos de
        agendamento</strong> para acelerar a configuracao. Eles nao sao regras
        definitivas: voce pode editar, excluir ou cadastrar outros tipos depois.
      </p>
      <p>Resumo das periodicidades:</p>
      <ul>
        <li><strong>Unico:</strong> dispara uma vez, em data fixa;</li>
        <li><strong>Diario / Semanal / Mensal / Anual:</strong> repete no periodo;</li>
        <li>
          <strong>Dias antes do vencimento:</strong> conta a partir do
          vencimento, pelo tipo ou pelo documento.
        </li>
      </ul>
      <p>
        No projeto, esses tipos sao vinculados aos documentos para gerar os
        lembretes de vencimento automaticamente. E possivel{" "}
        <strong>combinar tipos</strong>; para comecar, o mais flexivel costuma
        ser <strong>"Dias antes do vencimento (pelo documento)"</strong>, onde
        cada documento define seus dias de aviso.
      </p>
      <p>
        Nao esqueca de <strong>vincular os usuarios</strong> ao tipo de
        agendamento: sao eles que recebem o alerta.
      </p>
    </>
  ),
};

const TIPOS_PAGAMENTO_CONTEUDO = {
  info: (
    <>
      <p>
        Os tipos de pagamento indicam a <strong>periodicidade ou condicao de
        pagamento</strong> associada aos documentos e contratos.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui alguns <strong>exemplos de
        tipo de pagamento</strong>, como Mensal, Quinzenal, Semestral e Anual.
        Eles sao apenas um ponto de partida.
      </p>
      <p>
        O usuario tem autonomia para modificar esses exemplos depois: pode
        editar nomes, inativar o que nao fizer sentido para a operacao ou
        cadastrar novos ciclos de pagamento usados pela entidade.
      </p>
      <p>
        No projeto, esse cadastro ajuda a classificar o comportamento financeiro
        do documento, facilitando filtros, relatorios e a leitura dos contratos.
        Se a entidade nao controla periodicidade de pagamento, esta etapa pode
        ser marcada como configurada sem prejuizo.
      </p>
    </>
  ),
};

const FILIAIS_CONTEUDO = {
  info: (
    <>
      <p>
        Filiais separam os <strong>documentos por unidade</strong>, mesmo quando
        a entidade opera apenas com a matriz.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui uma <strong>filial padrao</strong>
        usando razao social, nome fantasia e <strong>o mesmo CNPJ da entidade</strong>.
        Isso agiliza o cadastro dos documentos, porque sempre havera uma unidade
        disponivel para vincular.
      </p>
      <p>
        O usuario tem autonomia para ajustar essa filial depois, cadastrar outras
        unidades ou inativar o que nao fizer sentido para a operacao. O limite de
        filiais depende do plano escolhido.
      </p>
    </>
  ),
};

const INDICES_CORRECAO_CONTEUDO = {
  info: (
    <>
      <p>
        Indices de correcao podem ser associados aos contratos para acompanhar
        reajustes, revisoes de valor e atualizacoes monetarias.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui alguns <strong>exemplos
        comuns</strong>: INPC, IPCA, INCC e IGPM. Eles servem como ponto de
        partida para a configuracao.
      </p>
      <p>
        O usuario tem autonomia para editar, inativar ou cadastrar outros
        indices conforme a regra dos seus contratos. Se a entidade nao usa
        reajuste por indice, basta marcar a etapa como configurada.
      </p>
    </>
  ),
};

const CLASSIFICACAO_TIPOS_DOCUMENTO_CONTEUDO = {
  info: (
    <>
      <p>
        As classificacoes <strong>agrupam os tipos de documento</strong> e ajudam
        a organizar filtros, paineis e relatorios.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui exemplos iniciais:{" "}
        <strong>Empresa</strong>, <strong>Funcionarios</strong> e{" "}
        <strong>Outros</strong>.
      </p>
      <p>
        O usuario tem autonomia para renomear, inativar ou cadastrar novas
        classificacoes conforme a operacao. Depois, cada tipo de documento fica
        vinculado a uma dessas classificacoes.
      </p>
    </>
  ),
};

const TIPOS_DOCUMENTO_CONTEUDO = {
  info: (
    <>
      <p>
        Os tipos de documento sao <strong>o coracao do sistema</strong>: todo
        documento cadastrado pertence a um tipo.
      </p>
      <p>
        Ao abrir esta etapa, o sistema ja inclui exemplos iniciais:{" "}
        <strong>Alvara de Funcionamento</strong>, <strong>Apolice de Seguro</strong>,{" "}
        <strong>NR 12</strong> e <strong>CNH</strong>.
      </p>
      <p>
        Esses exemplos sao apenas um ponto de partida. O usuario pode editar,
        inativar ou cadastrar outros tipos de documento conforme a rotina da
        entidade. Cada tipo fica vinculado a uma classificacao e pode ter seus
        proprios campos e tipos de agendamento.
      </p>
    </>
  ),
};

const CONTEUDO = {
  visao_geral: {
    tipo: "resumo",
    info: (
      <>
        <p>
          O <strong>Doc</strong> centraliza os documentos e contratos da sua
          entidade e <strong>avisa por e-mail antes dos vencimentos</strong> —
          nada mais de multa ou renovação perdida por esquecimento.
        </p>
        <p>Com a configuração inicial você terá:</p>
        <ul>
          <li>documentos organizados por tipo e classificação;</li>
          <li>alertas de vencimento automáticos para as pessoas certas;</li>
          <li>painéis e estatísticas da situação dos seus documentos.</li>
        </ul>
        <p>
          Este assistente conduz as etapas na ordem recomendada. Após a sua
          configuração, Você acessar as mesmas opções no Menu do sistema e 
          complementar os cadastros e configurações a a qualquer momento.
        </p>
      </>
    ),
  },
  plano: {
    tipo: "modal-preferencias",
    abaInicial: "plano",
    info: (
      <>
        <p>
          O plano define os <strong>limites da sua entidade</strong>: quantidade
          de usuários, filiais, documentos ativos e tamanho de arquivo.
        </p>
        <p>
          Você pode começar no plano <strong>gratuito</strong> e mudar quando
          precisar — o histórico e as configurações são preservados.
        </p>
      </>
    ),
  },
  grupos_usuario: {
    Componente: GruposUsuario,
    info: (
      <>
        <p>
          Dois grupos já foram criados automaticamente com a sua conta:{" "}
          <strong>Administradores</strong> (acesso total) e{" "}
          <strong>Acesso Normal</strong> — mesmas permissões, exceto{" "}
          <strong>Entidades, Grupos de Usuário e Usuários</strong>. Assim quem
          estiver no Acesso Normal não altera plano, não cria usuários e não
          muda permissões de outros grupos.
        </p>
        <p>
          Você pode usar os dois como estão, ajustar as permissões de qualquer
          um deles, ou criar outros grupos para necessidades específicas.
        </p>
      </>
    ),
  },
  usuarios: {
    Componente: Usuarios,
    info: (
      <>
        <p>
          Cadastre as pessoas que vão usar o sistema —{" "}
          <strong>são os usuários que recebem os alertas por e-mail</strong>.
        </p>
        <p>Existem 3 tipos de usuário:</p>
        <ul>
          <li>
            <strong>Administrador (A):</strong> acesso total à entidade;
          </li>
          <li>
            <strong>Normal (N):</strong> acessa conforme as permissões do grupo;
          </li>
          <li>
            <strong>Comunicado (C):</strong> não faz login — serve apenas para{" "}
            <em>receber os alertas por e-mail</em> (ilimitados no plano).
          </li>
        </ul>
      </>
    ),
  },
  tipos_agendamento: {
    Componente: TiposAgendamento,
    info: (
      <>
        <p>
          Os tipos de agendamento definem <strong>quando os alertas de
          vencimento são disparados</strong> e para quem.
        </p>
        <p>Resumo das periodicidades:</p>
        <ul>
          <li><strong>Único:</strong> dispara uma vez, em data fixa;</li>
          <li><strong>Diário / Semanal / Mensal / Anual:</strong> repete no período;</li>
          <li>
            <strong>Dias antes do vencimento:</strong> conta a partir do
            vencimento — <em>pelo tipo</em> (V) ou <em>pelo documento</em> (VD).
          </li>
        </ul>
        <p>
          É possível <strong>combinar tipos</strong>, mas para começar sugerimos{" "}
          <strong>"Dias antes do vencimento (pelo documento)"</strong> — cada
          documento define seus dias de aviso.
        </p>
        <p>
          Não esqueça de <strong>vincular os usuários</strong> ao tipo de
          agendamento: são eles que recebem o alerta.
        </p>
      </>
    ),
    ...TIPOS_AGENDAMENTO_CONTEUDO,
  },
  tipos_pagamento: {
    Componente: TiposPagamento,
    info: (
      <>
        <p>
          Os tipos de pagamento indicam a <strong>periodicidade ou condição de
          pagamento</strong> associada aos documentos e contratos.
        </p>
        <p>
          Exemplos: <strong>Mensal</strong>, <strong>Quinzenal</strong>,{" "}
          <strong>Semestral</strong>, <strong>Anual</strong>, pagamento único ou
          qualquer outro ciclo usado pela sua operação.
        </p>
        <p>
          <strong>Não é obrigatório</strong> cadastrar nem usar — se a sua
          operação não controla recorrência ou ciclo de pagamento, pule a etapa.
        </p>
      </>
    ),
    ...TIPOS_PAGAMENTO_CONTEUDO,
  },
  tipos_fornecedor: {
    Componente: TiposFornecedor,
    info: (
      <>
        <p>
          Categorias para organizar seus fornecedores (ex.: Serviços,
          Materiais, Software).
        </p>
        <p>
          <strong>Não é obrigatório</strong> cadastrar outros — pule se não
          precisar agora.
        </p>
      </>
    ),
  },
  fornecedores: {
    Componente: Fornecedores,
    info: (
      <>
        <p>
          Fornecedores são as empresas com quem você mantém{" "}
          <strong>contratos e documentos</strong> — cada documento pode ser
          vinculado ao fornecedor correspondente.
        </p>
        <p>
          Exemplo: o contrato de limpeza fica vinculado ao fornecedor
          "Empresa de Limpeza XYZ", e o alerta de renovação chega antes do fim
          da vigência.
        </p>
        <p>
          Ou ainda, você precisa controlar a documentação trabalhista dos funcionarios
          desta empresa que prestam serviço a você, gerenciando os vencimentos destes 
          documentos.
        </p>
        <p><strong>Não é obrigatório</strong> — cadastre quando precisar.</p>
      </>
    ),
  },
  funcionarios: {
    Componente: Funcionarios,
    info: (
      <>
        <p>
          Vincule documentos a <strong>funcionários</strong> — ideal para
          documentos de RH como ASO, contratos de trabalho e certificados.
        </p>
        <p><strong>Não é obrigatório</strong> — pule se não fizer sentido agora.</p>
      </>
    ),
  },
  empresas_filiais: {
    Componente: Filiais,
    info: (
      <>
        <p>
          Cadastre as <strong>filiais</strong> da sua entidade para separar os
          documentos de cada unidade.
        </p>
        <p>
          O limite de filiais depende do plano escolhido.{" "}
          <strong>Não é obrigatório</strong> se você opera com uma única unidade.
        </p>
      </>
    ),
    ...FILIAIS_CONTEUDO,
  },
  indices_correcao: {
    Componente: IndicesCorrecao,
    info: (
      <>
        <p>
          Índices de correção podem ser associados aos contratos para acompanhar
          reajustes.
        </p>
        <p>
          Use <strong>Usar modelo</strong> para começar com índices comuns, como{" "}
          <strong>INPC</strong>, <strong>IPCA</strong>, <strong>INCC</strong> e{" "}
          <strong>IGPM</strong>.
        </p>
        <p>
          <strong>Não é obrigatório</strong> — se seus contratos não usam
          reajuste por índice, pule esta etapa.
        </p>
      </>
    ),
    ...INDICES_CORRECAO_CONTEUDO,
  },
  campos_customizados: {
    Componente: CamposCustomizados,
    info: (
      <>
        <p>
          Crie <strong>campos extras por tipo de documento</strong> — ex.:
          "Placa do veículo" no CRLV, "Valor pago" no recibo.
        </p>
        <p><strong>Não é obrigatório</strong> — configure quando sentir falta de um campo.</p>
      </>
    ),
  },
  preferencias_entidade: {
    tipo: "modal-preferencias",
    abaInicial: "geral",
    info: (
      <>
        <p>Ajuste as preferências gerais da entidade:</p>
        <ul>
          <li>tamanho máximo de arquivo e nº de arquivos por documento;</li>
          <li><strong>LGPD:</strong> ocultar dados pessoais em telas e exportações;</li>
          <li>linhas por página nas listagens e logotipo.</li>
        </ul>
      </>
    ),
  },
  email_notificacao: {
    Componente: EmailNotificacao,
    info: (
      <>
        <p>
          Configure o <strong>remetente (SMTP)</strong> dos alertas —{" "}
          <strong>sem isso, os avisos de vencimento não são enviados</strong>.
        </p>
        <p>
          Use uma conta de e-mail da sua organização (ex.:
          alertas@suaempresa.com.br) e faça o teste de envio após salvar.
        </p>
        <p>
          Se nenhum e-mail for configurado, a entidade poderá enviar até{" "}
          <strong>100 e-mails de notificação</strong> usando o remetente padrão.
          Após esse limite, será necessário configurar um e-mail próprio para
          continuar enviando alertas.
        </p>
      </>
    ),
  },
  classificacao_tipos_documento: {
    Componente: ClassificacaoTiposDocumento,
    info: (
      <>
        <p>
          As classificações <strong>agrupam os tipos de documento</strong> nos
          filtros e painéis.
        </p>
        <p>
          Sugestao para comecar: use o modelo com{" "}
          <strong>Empresa</strong>, <strong>Funcionarios</strong> e{" "}
          <strong>Outros</strong>.
        </p>
      </>
    ),
    ...CLASSIFICACAO_TIPOS_DOCUMENTO_CONTEUDO,
  },
  tipos_documento: {
    Componente: TiposDocumento,
    info: (
      <>
        <p>
          Os tipos de documento são <strong>o coração do sistema</strong> — todo
          documento cadastrado pertence a um tipo.
        </p>
        <p>
          Exemplos: <em>Contrato de Prestação de Serviço</em>, <em>Alvará</em>,{" "}
          <em>Apólice de Seguro</em>, <em>ASO</em>.
        </p>
        <p>
          Use o modelo para iniciar com <strong>Alvara de Funcionamento</strong>,{" "}
          <strong>Apolice de Seguro</strong>, <strong>NR 12</strong> e{" "}
          <strong>CNH</strong>.
        </p>
        <p>Cada tipo é vinculado a uma classificação criada na etapa anterior.</p>
      </>
    ),
    ...TIPOS_DOCUMENTO_CONTEUDO,
  },
  documentos: {
    Componente: Documentos,
    info: (
      <>
        <p>
          Tudo pronto! Cadastre o <strong>primeiro documento</strong> e veja o
          sistema funcionando: anexe o PDF, defina o vencimento e os dias de
          aviso.
        </p>
        <p>
          A partir daqui os alertas por e-mail e os painéis passam a trabalhar
          por você.
        </p>
      </>
    ),
  },
};

export function obterConteudo(chave) {
  return CONTEUDO[chave] || null;
}

export default CONTEUDO;
